import {
  STATIONS,
  fadedRunPaces,
  formatDuration,
  type Plan,
  type StationTarget,
} from "@roxpacer/core";
import { useState, type ReactNode } from "react";
import { useEstimate } from "../lib/use-estimate";
import { useActivePlan, usePlans } from "../store";
import { DurationInput } from "./DurationInput";

const FADE_OPTIONS = [0, 1, 2, 3, 4, 5];

function RunsToolbar() {
  const plan = useActivePlan();
  const updateActive = usePlans((s) => s.updateActive);
  const [pace, setPace] = useState(plan.runs[0]?.paceSecPerKm ?? 330);
  const [fade, setFade] = useState(0);

  return (
    <div className="card flex flex-wrap items-center gap-x-3 gap-y-2 p-3 text-sm">
      <span className="font-medium">Set all runs</span>
      <DurationInput label="Pace for all runs" value={pace} onCommit={setPace} suffix="/km" />
      <label className="flex items-center gap-1.5 text-ink-2">
        slowing
        <select
          className="field h-9 pr-1"
          value={fade}
          onChange={(e) => setFade(Number(e.target.value))}
          aria-label="Slowdown per run"
        >
          {FADE_OPTIONS.map((pct) => (
            <option key={pct} value={pct}>
              {pct}%
            </option>
          ))}
        </select>
        per run
      </label>
      <button
        type="button"
        className="btn ml-auto"
        onClick={() =>
          updateActive((p) => ({
            ...p,
            runs: fadedRunPaces(pace, fade).map((paceSecPerKm) => ({ paceSecPerKm })),
          }))
        }
      >
        Apply
      </button>
    </div>
  );
}

function SplitSlider({ plan, index }: { plan: Plan; index: number }) {
  const updateActive = usePlans((s) => s.updateActive);
  const [a, b] = plan.athletes;
  const station = plan.stations[index]!;
  if (!a || !b) return null;
  const aPct = station.split?.find((s) => s.athleteId === a.id)?.pct ?? 50;

  const setSplit = (pct: number) =>
    updateActive((p) => ({
      ...p,
      stations: p.stations.map((s, i) =>
        i === index
          ? {
              ...s,
              split: [
                { athleteId: a.id, pct },
                { athleteId: b.id, pct: 100 - pct },
              ],
            }
          : s,
      ),
    }));

  return (
    <div className="flex items-center gap-2 text-xs text-ink-2 sm:col-span-3">
      <span className="w-24 truncate text-right" title={a.name}>
        {a.name} <span className="tabular-nums text-ink">{Math.round(aPct)}%</span>
      </span>
      <input
        type="range"
        min={0}
        max={100}
        step={5}
        value={aPct}
        onChange={(e) => setSplit(Number(e.target.value))}
        aria-label={`${STATIONS[index]!.name} work split`}
        aria-valuetext={`${a.name} ${Math.round(aPct)}%, ${b.name} ${Math.round(100 - aPct)}%`}
        className="h-1.5 flex-1 accent-accent"
      />
      <span className="w-24 truncate" title={b.name}>
        <span className="tabular-nums text-ink">{Math.round(100 - aPct)}%</span> {b.name}
      </span>
    </div>
  );
}

export function LegEditor() {
  const plan = useActivePlan();
  const estimate = useEstimate();
  const updateActive = usePlans((s) => s.updateActive);

  const setStation = (index: number, change: Partial<StationTarget>) =>
    updateActive((p) => ({
      ...p,
      stations: p.stations.map((s, i) => (i === index ? { ...s, ...change } : s)),
    }));
  const setRun = (index: number, paceSecPerKm: number) =>
    updateActive((p) => ({
      ...p,
      runs: p.runs.map((r, i) => (i === index ? { paceSecPerKm } : r)),
    }));
  const athleteName = (id: string) => plan.athletes.find((a) => a.id === id)?.name ?? "";

  return (
    <section className="space-y-3" aria-labelledby="legs-heading">
      <h2 id="legs-heading" className="text-sm font-semibold tracking-wide text-ink-2 uppercase">
        Race plan
      </h2>
      <RunsToolbar />
      <ol className="space-y-2">
        {STATIONS.map((station, i) => {
          const leg = i + 1;
          const stationSegment = estimate.segments.find((s) => s.id === `station-${leg}`);
          const target = plan.stations[i]!;
          return (
            <li key={station.id} className="card p-3">
              <div className="mb-2.5 flex items-baseline gap-2">
                <span className="grid size-6 shrink-0 place-items-center rounded-full bg-surface-2 text-xs font-semibold tabular-nums">
                  {leg}
                </span>
                <h3 className="font-semibold">{station.name}</h3>
                <span className="text-xs text-muted">
                  {station.amount}
                  {station.unit === "m" ? " m" : " reps"}
                </span>
                {plan.format === "relay" && stationSegment && (
                  <span className="rounded-full bg-surface-2 px-2 py-0.5 text-xs text-ink-2">
                    {athleteName(stationSegment.athleteIds[0]!)}
                  </span>
                )}
                <span className="ml-auto text-xs text-muted">
                  at{" "}
                  <span className="font-medium text-ink tabular-nums">
                    {formatDuration(stationSegment?.endSec ?? 0)}
                  </span>
                </span>
              </div>
              <div className="grid grid-cols-[auto_1fr] items-center gap-x-3 gap-y-2 sm:grid-cols-3 sm:gap-x-4">
                <Field color="bg-run" label={`Run ${leg}`}>
                  <DurationInput
                    label={`Run ${leg} pace`}
                    suffix="/km"
                    max={3600}
                    value={plan.runs[i]!.paceSecPerKm}
                    onCommit={(sec) => setRun(i, sec)}
                  />
                </Field>
                <Field color="bg-roxzone" label="Roxzone">
                  <DurationInput
                    label={`Roxzone ${leg} time`}
                    allowZero
                    max={3600}
                    value={target.roxzoneSec}
                    onCommit={(roxzoneSec) => setStation(i, { roxzoneSec })}
                  />
                </Field>
                <Field color="bg-station" label="Station">
                  <DurationInput
                    label={`${station.name} time`}
                    value={target.targetSec}
                    onCommit={(targetSec) => setStation(i, { targetSec })}
                  />
                </Field>
                {plan.format === "doubles" && <SplitSlider plan={plan} index={i} />}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function Field({ color, label, children }: { color: string; label: string; children: ReactNode }) {
  return (
    <div className="col-span-2 flex items-center justify-between gap-2 sm:col-span-1 sm:justify-start">
      <span className="flex items-center gap-1.5 text-sm text-ink-2">
        <span aria-hidden className={`h-3 w-1 rounded-full ${color}`} />
        {label}
      </span>
      {children}
    </div>
  );
}
